import { useQueryClient } from '@tanstack/react-query';
import { UzButton, UzCard, UzSelect, UzSpinner, UzBadge } from '@utazzo/ui';
import {
  ArrowLeft,
  Check,
  Cloud,
  FileSpreadsheet,
  Inbox,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { JobSummary } from '../components/JobSummary';
import { OperatorGuideButton } from '../components/OperatorGuideButton';
import { RowCard } from '../components/RowCard';
import { SampleTemplateButton } from '../components/SampleTemplateButton';
import { leadImportPreviewColumns } from '../lead-import-preview';
import {
  useCommitImport,
  useImportJob,
  useImportModules,
  useImportRows,
  useRetryFailedImport,
  useRevalidateImport,
  useUploadImport,
  invalidateLeadsAfterImport,
} from '../use-imports';

import { PageShell } from '@/components/page-shell';
import { importJobPath } from '@/features/imports/import-module-routes';
import { useListReturn } from '@/hooks/use-list-return';

/** PascalCase header from the Meta template (e.g. CustomerName), when present. */
const metaHeaderAlias = (c: { label: string; aliases?: ReadonlyArray<string> }): string | null => {
  const hit = c.aliases?.find((a) => /^[A-Z][A-Za-z0-9]*$/.test(a) && a !== c.label);
  return hit ?? null;
};

/**
 * Legacy sheet headers we still parse on upload but do not list in the
 * Meta upload guide — the lead row only stores first + last name.
 */
const LEGACY_UPLOAD_GUIDE_FIELDS = new Set(['firstName', 'lastName', 'adId', 'adName']);

const importTargetLabel = (field: string): string => {
  if (field === 'customerName') return 'firstName + lastName';
  return field;
};

/**
 * Lead Imports wizard (ADR-0036). Single-page flow:
 *   1. /leads/imports/new            → Upload step.
 *   2. /leads/imports/jobs/:jobId    → Preview / Fix / Commit step.
 *
 * Lead import is **single-sheet** (legacy XLSX format). For multi-sheet
 * importers (Hotels and onward), see ImportHotelsPage which shares the
 * row card + counter components.
 */
export const ImportLeadsPage = (): React.JSX.Element => {
  const { jobId } = useParams<{ jobId?: string }>();
  if (!jobId) return <UploadStep />;
  return <ReviewStep jobId={jobId} />;
};

// ---------- Step 1: upload ----------

const UploadStep = (): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn('/leads');
  const modulesQuery = useImportModules();
  const upload = useUploadImport();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [moduleKey, setModuleKey] = useState<string>('lead');

  const moduleOptions = useMemo(
    () =>
      (modulesQuery.data ?? [])
        .filter((m) => !m.sheets || m.sheets.length === 0)
        .map((m) => ({ value: m.moduleKey, label: m.displayName })),
    [modulesQuery.data],
  );
  const activeModule = (modulesQuery.data ?? []).find((m) => m.moduleKey === moduleKey);

  const handleUpload = async (): Promise<void> => {
    if (!file) return;
    try {
      const { jobId } = await upload.mutateAsync({ moduleKey, file });
      navigate(`/leads/imports/jobs/${jobId}`);
    } catch {
      // The mutation's onError already surfaced a toast; swallow here so a
      // failed upload (e.g. a slow-network timeout) doesn't bubble up as an
      // "unhandled promise rejection". Nothing else to do - the user stays
      // on the upload step and can retry.
    }
  };

  return (
    <PageShell
      title="Import leads"
      description="Upload an XLSX, validate every row, fix the bad ones, then commit."
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <Inbox className="mr-1.5 h-4 w-4" /> Lead inbox
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => navigate('/leads/imports')}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Audit log
          </UzButton>
        </div>
      }
    >
      <div className="mx-auto max-w-3xl space-y-4">
        <UzCard title="Module">
          <UzSelect
            name="moduleKey"
            label="Importing into"
            options={moduleOptions}
            value={moduleKey}
            onValueChange={(v) => v && setModuleKey(v)}
            disabled={modulesQuery.isLoading || upload.isPending}
          />
          {activeModule?.description && (
            <p className="mt-2 text-xs text-zinc-500">{activeModule.description}</p>
          )}
        </UzCard>

        <UzCard
          title="XLSX file"
          description={`Max 10MB / 5000 rows. The first sheet is used. Headers may use the Meta machine keys (CreatedOn, CustomerName, Destination, …) or the human labels from row 2 of the template- that decorative label row is skipped automatically.`}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          <div className="flex items-center gap-3">
            <UzButton variant="outline" onClick={() => fileRef.current?.click()}>
              <FileSpreadsheet className="mr-1.5 h-4 w-4" />
              Pick XLSX
            </UzButton>
            <div className="text-sm text-zinc-700">
              {file ? (
                <>
                  <strong>{file.name}</strong>
                  <span className="ml-2 text-zinc-500">{(file.size / 1024).toFixed(1)} KB</span>
                </>
              ) : (
                'No file selected.'
              )}
            </div>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="text-xs text-zinc-500 hover:text-zinc-700"
              >
                Clear
              </button>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <SampleTemplateButton moduleKey={moduleKey} />
              <OperatorGuideButton moduleKey={moduleKey} />
            </div>
            <UzButton onClick={handleUpload} loading={upload.isPending} disabled={!file}>
              <Cloud className="mr-1.5 h-4 w-4" />
              Upload + validate
            </UzButton>
          </div>
        </UzCard>

        {activeModule &&
          (() => {
            const guideColumns = activeModule.columns.filter(
              (c) => !LEGACY_UPLOAD_GUIDE_FIELDS.has(c.field),
            );
            const requiredCount = guideColumns.filter((c) => c.required).length;
            return (
              <UzCard
                title="Expected columns"
                description={`${guideColumns.length} columns in the Meta template (${requiredCount} required). Customer Name is split into first + last on the lead. Legacy files with separate first/last name or Ad ID columns still import.`}
              >
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {guideColumns.map((c) => {
                    const metaHeader = metaHeaderAlias(c);
                    return (
                      <div
                        key={c.field}
                        className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm"
                      >
                        <span className="text-zinc-700">
                          {c.label}
                          {metaHeader ? (
                            <span className="ml-1 font-mono text-xs text-zinc-500">
                              ({metaHeader})
                            </span>
                          ) : null}
                        </span>
                        {c.required && <UzBadge tone="destructive">required</UzBadge>}
                        <span className="text-xs text-zinc-400">
                          → {importTargetLabel(c.field)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </UzCard>
            );
          })()}
      </div>
    </PageShell>
  );
};

// ---------- Step 2: review ----------

const STATUS_FILTERS = [
  { value: 'ALL', label: 'All rows' },
  { value: 'INVALID', label: 'Invalid' },
  { value: 'VALID', label: 'Valid' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'COMMITTED', label: 'Committed' },
  { value: 'COMMIT_FAILED', label: 'Failed' },
] as const;

const ReviewStep = ({ jobId }: { jobId: string }): React.JSX.Element => {
  const navigate = useNavigate();
  const backToList = useListReturn('/leads');
  const qc = useQueryClient();
  const job = useImportJob(jobId);
  const prevStatusRef = useRef<string | undefined>(undefined);
  const modulesQuery = useImportModules();
  const [statusFilter, setStatusFilter] =
    useState<(typeof STATUS_FILTERS)[number]['value']>('INVALID');
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);
  const rows = useImportRows(jobId, {
    page,
    pageSize: 25,
    sortBy: 'rowIndex',
    sortDir: 'asc',
    status: statusFilter === 'ALL' ? undefined : statusFilter,
  });
  const commit = useCommitImport(jobId);
  const retryFailed = useRetryFailedImport(jobId);
  const revalidate = useRevalidateImport(jobId);

  // Async queue path: commit returns while status=COMMITTING. Refresh
  // lead lists once the worker finishes so the inbox picks up new rows.
  useEffect(() => {
    const status = job.data?.status;
    const prev = prevStatusRef.current;
    if (prev === 'COMMITTING' && status === 'COMPLETED') {
      invalidateLeadsAfterImport(qc);
      qc.invalidateQueries({ queryKey: ['imports', 'jobs', jobId, 'rows'] });
      const committed = job.data?.committedRows ?? 0;
      const failed = job.data?.failedRows ?? 0;
      if (committed > 0) {
        toast.success(
          `Imported ${committed} lead(s).${failed ? ` ${failed} row(s) failed.` : ''} View them in Lead inbox.`,
        );
      } else if (failed > 0) {
        toast.error(`${failed} row(s) failed to import. Filter by Failed to see why.`);
      }
    }
    prevStatusRef.current = status;
  }, [job.data?.status, job.data?.committedRows, job.data?.failedRows, jobId, qc]);

  const moduleDescriptor = (modulesQuery.data ?? []).find(
    (m) => m.moduleKey === job.data?.moduleKey,
  );
  const columns = moduleDescriptor?.columns ?? [];

  // This page renders single-sheet modules only. A multi-sheet job (hotel)
  // reached here through a stale or cross-module link has no columns to show -
  // the row body would come up empty - so send it to its own module's page.
  const isMultiSheet = (moduleDescriptor?.sheets?.length ?? 0) > 0;
  useEffect(() => {
    if (job.data && isMultiSheet) {
      navigate(importJobPath(job.data.moduleKey, jobId), { replace: true });
    }
  }, [job.data, isMultiSheet, jobId, navigate]);

  const canCommit =
    !!job.data &&
    (job.data.status === 'READY' || job.data.status === 'PARTIAL') &&
    job.data.invalidRows === 0 &&
    job.data.validRows > 0;

  const isCommitting = job.data?.status === 'COMMITTING';

  // Retry surfaces whenever the job has rows that failed to commit and no
  // commit is currently running. One click resets those rows and re-commits.
  const canRetry = !!job.data && job.data.failedRows > 0 && !isCommitting;

  if (job.isLoading || !job.data) {
    return (
      <PageShell title="Loading import job…" description="">
        <UzCard>
          <div className="flex items-center gap-2 p-6 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Fetching status…
          </div>
        </UzCard>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={`Import - ${job.data.fileName}`}
      description={`${job.data.totalRows} rows · ${job.data.moduleKey} module`}
      actions={
        <div className="flex gap-2">
          <UzButton variant="ghost" size="sm" onClick={backToList}>
            <Inbox className="mr-1.5 h-4 w-4" /> Lead inbox
          </UzButton>
          <UzButton variant="ghost" size="sm" onClick={() => navigate('/leads/imports')}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Audit log
          </UzButton>
          <UzButton
            variant="outline"
            size="sm"
            onClick={() => revalidate.mutateAsync().catch(() => undefined)}
            loading={revalidate.isPending}
            title="Re-check every row against the current masters and rules - clears errors fixed since upload, no re-upload needed."
          >
            <RefreshCw className="mr-1.5 h-4 w-4" /> Re-validate
          </UzButton>
          <UzButton
            onClick={() => commit.mutateAsync()}
            loading={commit.isPending || isCommitting}
            disabled={!canCommit || isCommitting}
          >
            <Check className="mr-1.5 h-4 w-4" />
            {isCommitting ? 'Importing…' : `Commit ${job.data.validRows} row(s)`}
          </UzButton>
          {canRetry && (
            <UzButton
              variant="outline"
              onClick={() => retryFailed.mutateAsync().catch(() => undefined)}
              loading={retryFailed.isPending}
              disabled={retryFailed.isPending}
            >
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Retry {job.data.failedRows} failed row(s)
            </UzButton>
          )}
          {job.data.status === 'COMPLETED' && job.data.committedRows > 0 && (
            <UzButton
              size="sm"
              onClick={() => navigate('/leads?tab=pipeline&daterange=this_month')}
            >
              View in Lead inbox
            </UzButton>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        <JobSummary job={job.data} />

        {(isCommitting || job.data.status === 'COMPLETED') && (
          <CommitProgress
            done={job.data.committedRows + job.data.failedRows + job.data.skippedRows}
            total={job.data.validRows}
            committed={job.data.committedRows}
            failed={job.data.failedRows}
            completed={job.data.status === 'COMPLETED'}
          />
        )}

        <UzCard
          title="Rows"
          actions={
            <div className="w-40">
              <UzSelect
                name="status-filter"
                options={
                  STATUS_FILTERS as unknown as ReadonlyArray<{ value: string; label: string }>
                }
                value={statusFilter}
                onValueChange={(v) =>
                  v && setStatusFilter(v as (typeof STATUS_FILTERS)[number]['value'])
                }
              />
            </div>
          }
        >
          {rows.isLoading ? (
            <div className="flex items-center gap-2 py-4 text-sm text-zinc-500">
              <UzSpinner size="sm" /> Loading rows…
            </div>
          ) : (rows.data?.data ?? []).length === 0 ? (
            <div className="py-6 text-center text-sm text-zinc-500">No rows match this filter.</div>
          ) : (
            <>
              <div className="space-y-2">
                {(rows.data?.data ?? []).map((row) => (
                  <RowCard
                    key={row.id}
                    row={row}
                    columns={columns}
                    jobId={jobId}
                    filterColumns={leadImportPreviewColumns}
                    summaryFields={['customerName', 'firstName', 'lastName', 'mobileNumber']}
                  />
                ))}
              </div>
              {(rows.data?.pagination.totalPages ?? 1) > 1 && (
                <div className="mt-3 flex items-center justify-between border-t border-zinc-100 pt-3 text-xs text-zinc-600">
                  <span>
                    Page {rows.data?.pagination.page} of {rows.data?.pagination.totalPages} ·{' '}
                    {rows.data?.pagination.totalItems} row
                    {rows.data?.pagination.totalItems === 1 ? '' : 's'} total
                  </span>
                  <div className="flex gap-1">
                    <UzButton
                      size="sm"
                      variant="ghost"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={!rows.data?.pagination.hasPrev || rows.isFetching}
                    >
                      Prev
                    </UzButton>
                    <UzButton
                      size="sm"
                      variant="ghost"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={!rows.data?.pagination.hasNext || rows.isFetching}
                    >
                      Next
                    </UzButton>
                  </div>
                </div>
              )}
            </>
          )}
        </UzCard>
      </div>
    </PageShell>
  );
};

/**
 * Live commit progress bar. Shown while the job is COMMITTING (bar fills as
 * the server flushes `committedRows` every few rows, picked up by the job
 * poll) and once COMPLETED. `done` = committed + failed + skipped so the bar
 * reflects rows actually processed, not just successes.
 */
const CommitProgress = ({
  done,
  total,
  committed,
  failed,
  completed,
}: {
  done: number;
  total: number;
  committed: number;
  failed: number;
  completed: boolean;
}): React.JSX.Element => {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : completed ? 100 : 0;
  return (
    <div
      className={`rounded-md border px-4 py-3 ${
        completed ? 'border-emerald-200 bg-emerald-50/60' : 'border-blue-200 bg-blue-50/60'
      }`}
    >
      <div className="mb-1.5 flex items-center justify-between text-sm">
        <span className="font-medium text-zinc-800">
          {completed ? 'Import complete' : 'Importing leads…'}
        </span>
        <span className="text-zinc-600 tabular-nums">
          {committed.toLocaleString('en-IN')} / {total.toLocaleString('en-IN')} committed
          {failed > 0 ? ` · ${failed.toLocaleString('en-IN')} failed` : ''}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200">
        <div
          className={`h-full rounded-full transition-[width] duration-500 ${
            completed ? 'bg-emerald-500' : 'bg-blue-500'
          }`}
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {!completed && (
        // The server flushes the committed count every 20 rows (see
        // runCommitRowLoop's PROGRESS_FLUSH_EVERY), so the bar advances in
        // steps of 20 rather than one-by-one - call that out so the operator
        // doesn't read a paused-looking bar as stuck.
        <div className="mt-1.5 text-[11px] text-zinc-500">Progress updates every 20 rows.</div>
      )}
    </div>
  );
};
